import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { StorageServiceBrowser, StorageServiceComposite, useAsyncState } from '@gafe/common-ui';
import { now } from 'lodash';

import { HeartbeatService } from '@/services/Heartbeat/Heartbeat';
import { IHeartbeat } from '@/services/Heartbeat/IHeartbeat';

export function createDefaultHeartbeatService() {
  const targets = [StorageServiceBrowser.session(), StorageServiceBrowser.local()];

  return new HeartbeatService(new StorageServiceComposite(targets));
}

export const HeartbeatContext = createContext<IHeartbeat>(createDefaultHeartbeatService());

export interface useHeartbeatProps {
  onDeath: () => void;
  timeout?: number;
}

export function useHeartbeat({ onDeath, timeout = 30000 }: useHeartbeatProps) {
  const service = useContext(HeartbeatContext);

  const [expiration] = useAsyncState(() => service.getExpiration(), [service]);

  const id = useRef<NodeJS.Timeout | number>(Infinity);

  const check = useCallback(async () => {
    const current = now();

    const expirationDate = (await service.getExpiration()) ?? Infinity;

    const expired = expirationDate <= current;

    if (expired) {
      clearInterval(id.current);
      onDeath?.call(null);
    }
  }, [expiration]);

  useEffect(() => {
    clearInterval(id.current); // Clear previous interval before setting a new one

    id.current = setInterval(() => check(), timeout);

    return () => clearInterval(id.current);
  }, [check, expiration]);
}
